import { kpiApi } from '@/includes/kpiApi';

/**
 * User Time KPI Service
 * Handles all interactions with the User Time KPI microservice
 */
class UserTimeKpiService {
  constructor() {
    this.logger = console; // In production, use a proper logging service
  }

  /**
   * Get user's last week activity time
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Activity data for the last week
   */
  async getUserLastWeekActivity(userId) {
    try {
      // First try to get existing KPI data
      let response = await kpiApi.get(`/kpi/${userId}`, {
        params: {
          periodType: 'weekly',
        },
      });

      // If no data exists, calculate it automatically
      if (!response.data.success || !response.data.data) {
        response = await kpiApi.post(`/kpi/${userId}/calculate-and-get`, {
          periodType: 'weekly',
        });
      }

      return response.data;
    } catch (error) {
      this.logger.error(`[UserTimeKpiService] Error fetching last week activity for user ${userId}:`, error);
      throw new Error(`Failed to fetch user activity: ${error.message}`);
    }
  }

  /**
   * Get user's activity data for chart visualization
   * @param {string} userId - User ID
   * @param {string} periodType - 'daily', 'weekly', 'monthly', 'custom'
   * @param {Date} startDate - Start date for the period
   * @param {Date} endDate - End date for the period
   * @returns {Promise<Object>} Activity data for chart
   */
  async getUserActivityChart(userId, periodType = 'daily', startDate = null, endDate = null) {
    try {
      // If custom date range is provided, use calculate-and-get endpoint
      if (startDate && endDate) {
        const response = await kpiApi.post(`/kpi/${userId}/calculate-and-get`, {
          periodType: 'custom',
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        });
        return response.data;
      }

      // Otherwise, use standard period type
      const params = { periodType };
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();

      // Try to get existing data first
      let response;
      try {
        response = await kpiApi.get(`/kpi/${userId}`, { params });
        if (response.data.success && response.data.data) {
          return response.data;
        }
      } catch {
        // If GET fails, fall back to calculate-and-get
      }

      // Calculate and get if no existing data or GET failed
      response = await kpiApi.post(`/kpi/${userId}/calculate-and-get`, {
        periodType,
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: endDate ? endDate.toISOString() : undefined,
      });

      return response.data;
    } catch (error) {
      this.logger.error(`[UserTimeKpiService] Error fetching activity chart for user ${userId}:`, error);
      throw new Error(`Failed to fetch activity chart: ${error.message}`);
    }
  }

  /**
   * Get user's real-time activity stats
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Real-time activity statistics
   */
  async getUserRealTimeStats(userId) {
    try {
      const response = await kpiApi.get(`/kpi/${userId}/realtime`);

      return response.data;
    } catch (error) {
      this.logger.error(`[UserTimeKpiService] Error fetching real-time stats for user ${userId}:`, error);
      throw new Error(`Failed to fetch real-time stats: ${error.message}`);
    }
  }

  /**
   * Calculate and get KPI for a user
   * @param {string} userId - User ID
   * @param {string} periodType - 'daily', 'weekly', 'monthly'
   * @returns {Promise<Object>} Calculated KPI data
   */
  async calculateAndGetUserKpi(userId, periodType = 'daily') {
    try {
      const response = await kpiApi.post(`/kpi/${userId}/calculate-and-get`, {
        periodType,
      });

      return response.data;
    } catch (error) {
      this.logger.error(`[UserTimeKpiService] Error calculating KPI for user ${userId}:`, error);
      throw new Error(`Failed to calculate KPI: ${error.message}`);
    }
  }

  /**
   * Get activity stats (event counts) for a single user in a date range.
   * @param {string} userId - User ID
   * @param {string} periodType - 'daily', 'weekly', 'monthly', 'custom'
   * @param {Date|null} startDate
   * @param {Date|null} endDate
   * @returns {Promise<Object>} Activity counts
   */
  async getUserActivityStats(userId, periodType = 'weekly', startDate = null, endDate = null) {
    try {
      const payload = { periodType };
      if (startDate) payload.startDate = startDate.toISOString();
      if (endDate) payload.endDate = endDate.toISOString();

      const response = await kpiApi.post(`/kpi/${userId}/activity-stats`, payload);
      return response.data;
    } catch (error) {
      this.logger.error(`[UserTimeKpiService] Error fetching activity stats for user ${userId}:`, error);
      throw new Error(`Failed to fetch activity stats: ${error.message}`);
    }
  }

  /**
   * Get batch KPIs for multiple users (single API call)
   * @param {Array<string>} userIds - Array of user IDs
   * @param {string} periodType - 'all-time' | 'weekly' | 'monthly'
   * @returns {Promise<Object>} KPI data mapped by user ID
   */
  async getBatchKpis(userIds, periodType = 'all-time') {
    try {
      if (!userIds || !Array.isArray(userIds) || userIds.length === 0) {
        this.logger.warn('[UserTimeKpiService] No userIds provided for batch KPI fetch');
        return {};
      }

      const response = await kpiApi.post('/kpi/batch', {
        userIds,
        periodType,
      });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch batch KPIs');
      }

      // Return the data object directly (it's already a map of userId -> KPI)
      return response.data.data || {};
    } catch (error) {
      this.logger.error('[UserTimeKpiService] Error fetching batch KPIs:', error);
      throw new Error(`Failed to fetch batch KPIs: ${error.message}`);
    }
  }

  /**
   * Get multiple users' activity data for the last week (batch operation)
   * Returns activity-based data (login events, assessments, level progress, devtips, travelbuddies)
   * @param {Array<string>} userIds - Array of user IDs
   * @returns {Promise<Object>} Activity data mapped by user ID
   */
  async getMultipleUsersLastWeekActivity(userIds) {
    try {
      // Use batch API with 'weekly' period type (last 7 days)
      const batchData = await this.getBatchKpis(userIds, 'weekly');

      // The new API returns activity data directly in the correct format:
      // {
      //   loginEventsCount, submittedAssessmentsCount, levelProgressCount,
      //   devTipsCompletedCount, travelBuddyCompletedCount, hasActivity,
      //   sessionMetrics, metrics
      // }
      // Just pass it through to the component
      return batchData;
    } catch (error) {
      this.logger.error('[UserTimeKpiService] Error fetching multiple users activity:', error);
      throw new Error(`Failed to fetch multiple users activity: ${error.message}`);
    }
  }
}

// Export singleton instance
export const userTimeKpiService = new UserTimeKpiService();
export default userTimeKpiService;
